import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { distinctUntilChanged, firstValueFrom, skip, timeout } from 'rxjs';
import { AUTH_CLIENT_TOKEN, AuthClientProvider } from '../tokens/auth-client.token';
import { UserRole } from '../enums/user-role.enum';
import { MeResponseDto, UpdateMeDto } from './dto/me-response.dto';
import { UserModel } from './models/user.model';
import { UserHttpService } from './user-http.service';

// Twardy limit czekania na /users/me przy starcie - chroni bootstrap przed
// zawieszeniem na braku sieci (provideAppInitializer jest blokujący).
const LOAD_TIMEOUT_MS: number = 8000;

@Injectable({ providedIn: 'root' })
export class UserStateService {
  private readonly api: UserHttpService = inject(UserHttpService);
  private readonly authClient: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);

  private readonly userSignal: WritableSignal<UserModel | null> = signal<UserModel | null>(null);
  readonly user: Signal<UserModel | null> = this.userSignal.asReadonly();
  readonly isAdmin: Signal<boolean> = computed((): boolean => this.userSignal()?.role === UserRole.Admin);

  // Wołane z provideAppInitializer. Blokuje bootstrap do pierwszego rozwiązania
  // stanu auth; gdy zalogowany - czeka na /users/me. Podpina też reaktywne
  // przeładowanie na kolejnych zmianach auth (login/logout w trakcie sesji).
  async init(): Promise<void> {
    // skip(1) - pierwsza emisja (replay bieżącego stanu) obsłużona poniżej przez
    // await; tu reagujemy tylko na PÓŹNIEJSZE zmiany. distinctUntilChanged tnie
    // powtórki true→true z odświeżania tokenu (onIdTokenChanged), żeby nie
    // przeładowywać /users/me przy każdym refreshu.
    this.authClient.isAuthed$.pipe(distinctUntilChanged(), skip(1)).subscribe((authed: boolean): void => {
      if (authed) {
        void this.loadMe();
      } else {
        this.reset();
      }
    });

    const authed: boolean = await firstValueFrom(this.authClient.isAuthed$);
    if (authed) {
      await this.loadMe();
    } else {
      this.reset();
    }
  }

  async loadMe(): Promise<void> {
    try {
      const dto: MeResponseDto = await firstValueFrom(this.api.getMe().pipe(timeout(LOAD_TIMEOUT_MS)));
      this.userSignal.set(this.toModel(dto));
    } catch {}
  }

  async patchMe(body: UpdateMeDto): Promise<void> {
    const dto: MeResponseDto = await firstValueFrom(this.api.patchMe(body));
    this.userSignal.set(this.toModel(dto));
  }

  reset(): void {
    this.userSignal.set(null);
  }

  private toModel(dto: MeResponseDto): UserModel {
    return {
      id: dto.id,
      email: dto.email,
      displayName: dto.displayName,
      firstName: dto.firstName,
      lastName: dto.lastName,
      photoUrl: dto.photoUrl,
      language: dto.language,
      role: dto.role,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt
    };
  }
}
